import { useEffect, useRef, useState, type ReactNode } from "react";
import { Group, Stack, Text, Title, UnstyledButton } from "@mantine/core";
import classes from "./ActionCarousel.module.css";

export interface ActionCard {
  id: string;
  icon: ReactNode;
  title: string;
  description: ReactNode;
  onClick?: () => void;
}

interface ActionCarouselProps {
  heading: string;
  cards: ActionCard[];
}

export function ActionCarousel({ heading, cards }: ActionCarouselProps) {
  const stripRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const strip = stripRef.current;

    // happy-dom has no IntersectionObserver; without it the first dot stays active.
    if (!strip || typeof IntersectionObserver === "undefined") {
      return;
    }

    const items = Array.from(strip.children) as HTMLElement[];
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveIndex(items.indexOf(entry.target as HTMLElement));
          }
        });
      },
      { root: strip, threshold: 0.6 },
    );

    items.forEach((item) => observer.observe(item));

    return () => observer.disconnect();
  }, [cards.length]);

  // Nothing left to offer: the heading would otherwise sit above an empty strip.
  if (cards.length === 0) return null;

  return (
    <Stack gap="sm">
      <Title order={2} fz="h4" fw={700}>
        {heading}
      </Title>

      <div
        className={classes.strip}
        ref={stripRef}
        role="group"
        aria-label={heading}
        tabIndex={0}
      >
        {cards.map((card) => {
          const body = (
            <Stack gap="xs">
              <div className={classes.badge}>{card.icon}</div>
              <Stack gap={4}>
                <Text size="md" fw={700}>
                  {card.title}
                </Text>
                <Text size="sm" c="var(--wallet-color-neutrals-gray-600)">
                  {card.description}
                </Text>
              </Stack>
            </Stack>
          );

          return card.onClick ? (
            <UnstyledButton
              key={card.id}
              className={classes.card}
              onClick={card.onClick}
            >
              {body}
            </UnstyledButton>
          ) : (
            <div key={card.id} className={classes.card}>
              {body}
            </div>
          );
        })}
      </div>

      {cards.length > 1 && (
        <Group justify="center" gap={6} aria-hidden="true">
          {cards.map((card, index) => (
            <span
              key={card.id}
              className={
                index === activeIndex ? classes.dotActive : classes.dot
              }
            />
          ))}
        </Group>
      )}
    </Stack>
  );
}
